import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import { Observable } from "rxjs";

import {
  ConfirmationModalComponent,
  ConfirmationModalData,
  ConfirmationModalResult,
} from "@shared/components/confirmation-modal/confirmation-modal.component";
import { BaseModalService } from "./base-modal.service";

@Injectable({
  providedIn: "root",
})
export class ConfirmationModalService extends BaseModalService<
  ConfirmationModalComponent,
  ConfirmationModalResult
> {
  constructor() {
    super(inject(MatDialog));
  }

  protected getDefaultConfig(): MatDialogConfig {
    return {
      width: "476px",
      maxWidth: "90vw",
      disableClose: false,
      autoFocus: true,
      restoreFocus: true,
    };
  }

  open(
    data: ConfirmationModalData,
    config?: MatDialogConfig,
  ): Observable<ConfirmationModalResult | null | undefined> {
    return this.openModal(ConfirmationModalComponent, data, config);
  }

  confirmClientDeactivation(
    clientName: string,
  ): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: `Are you sure you want to deactivate ${clientName}?`,
      description:
        "This will prevent new orders from being submitted and remove all active configurations.",
      confirmText: "Yes, deactivate client",
      cancelText: "No, keep active",
    });
  }

  confirmVendorDeactivation(
    vendorName: string,
  ): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: `Are you sure you want to deactivate ${vendorName}?`,
      description: "This will remove all active configurations.",
      confirmText: "Yes, deactivate vendor",
      cancelText: "No, keep active",
    });
  }

  confirmDisableProduct(
    clientName: string,
  ): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: `Deactivate Foreclosure Title for ${clientName}?`,
      confirmText: "Yes, deactivate product",
      cancelText: "No, keep active",
    });
  }

  confirmBulkUpdate(): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: "Are you sure you want to apply bulk update?",
      description:
        "This will apply the changes to all selected records and overwrite any existing configurations.",
      confirmText: "Apply",
      cancelText: "Cancel",
      confirmButtonColor: "primary",
    });
  }

  confirmSaveAsDraft(): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: "Do you wish to stop editing and save progress as a draft?",
      confirmText: "Save and close",
      cancelText: "Back to editing",
      confirmButtonColor: "primary",
      titleColor: "gray",
    });
  }

  confirmDoneEditing(
    entity: "Client" | "Vendor",
  ): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: `${entity} Configuration Saved Successfully!`,
      confirmText: "Done",
      cancelText: "Back to editing",
      confirmButtonColor: "primary",
      titleColor: "gray",
    });
  }

  confirmCompleteTask(): Observable<
    ConfirmationModalResult | null | undefined
  > {
    return this.open({
      title: "Complete Task?",
      description: "Are you sure you want to mark this task as completed?",
      confirmText: "Complete task",
      cancelText: "Go back",
      confirmButtonColor: "success",
      titleColor: "primary",
    });
  }

  confirmDeleteTask(): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: "Delete Task?",
      description:
        "Are you certain you want to delete this task? This action cannot be undone.",
      confirmText: "Delete task",
      cancelText: "Cancel",
      confirmButtonColor: "warn",
      titleColor: "primary",
    });
  }

  confirmDeleteDocument(): Observable<
    ConfirmationModalResult | null | undefined
  > {
    return this.open({
      title: "Delete Document?",
      description:
        "Are you certain you want to delete this document? This action cannot be undone.",
      confirmText: "Delete document",
      cancelText: "Cancel",
      confirmButtonColor: "warn",
      titleColor: "primary",
    });
  }

  confirmCancelOrder(): Observable<ConfirmationModalResult | null | undefined> {
    return this.open({
      title: "Are you sure you want to cancel this order?",
      confirmText: "Cancel Order",
      cancelText: "Keep Order",
      confirmButtonColor: "primary",
      titleColor: "gray",
    });
  }
}
